import { memo, useCallback, useMemo, useState } from "react";
import { Icon } from "@/components/common/Icon";
import { DisclosureChevron } from "@/components/common/Disclosure";
import {
	ALL_PROBLEM_FILTER,
	buildProblemGroups,
	countProblems,
	filterProblemGroups,
	limitProblemGroups,
	lspRangeToMonacoRange,
	type ProblemEntry,
	type ProblemFileGroup,
	type ProblemFilter,
} from "@/lib/problems";
import { navigateToProblem } from "@/lib/pyright/problems-navigation";
import { ROW_HOVER_CLASS, ROW_SELECTED_CLASS } from "@/lib/row-styles";
import { useDiagnosticsStore } from "@/stores/useDiagnosticsStore";

const NEUTRAL_FILTER_STYLE: React.CSSProperties = {
	background: "var(--surface-2)",
	color: "var(--fg-2)",
	boxShadow: "var(--shadow-ring)",
};

const META_TEXT_STYLE: React.CSSProperties = {
	fontFamily: "var(--font-mono)",
	color: "var(--fg-3)",
	flexShrink: 0,
};

/** カウントバッジ。円形にするため最小幅と行の高さを同じ値から作る。 */
const COUNT_BADGE_SIZE = 16;

const COUNT_BADGE_STYLE: React.CSSProperties = {
	minWidth: COUNT_BADGE_SIZE,
	padding: "0 6px",
	borderRadius: "var(--radius-full)",
	background: "var(--accent-subtle)",
	color: "var(--accent)",
	fontFamily: "var(--font-mono)",
	fontSize: "var(--text-section)",
	fontWeight: 500,
	lineHeight: `${COUNT_BADGE_SIZE}px`,
	textAlign: "center",
};

/** 現在のワークスペースで検出された Python のエラーと警告を表示する。 */
export function ProblemsPane() {
	const environmentId = useDiagnosticsStore((state) => state.environmentId);
	const byPath = useDiagnosticsStore((state) => state.byPath);
	const [filter, setFilter] = useState<ProblemFilter>(ALL_PROBLEM_FILTER);
	const [collapsedPaths, setCollapsedPaths] = useState<Set<string>>(() => new Set());
	// 選択中のエントリが属するグループも一緒に保持し、ProblemGroupRow へは自グループの選択時だけ
	// 実際の entryKey を渡す (他グループには常に null を渡すことで、選択の切り替えで再レンダーが
	// 起きるのを選択前後の2グループだけに抑える)。
	const [selection, setSelection] = useState<{ groupPath: string; entryKey: string } | null>(null);
	const counts = useMemo(() => countProblems(byPath), [byPath]);
	// フィルタの切り替えだけでは診断の再走査・再整列をしない。整列済みグループを使い回し、
	// filterProblemGroups で安価に絞り込む。
	const groups = useMemo(() => buildProblemGroups(byPath), [byPath]);
	const limited = useMemo(
		() => limitProblemGroups(filterProblemGroups(groups, filter)),
		[groups, filter],
	);
	const totalCount = counts.error + counts.warning;

	const toggleCollapsed = useCallback((path: string) => {
		setCollapsedPaths((paths) => {
			const next = new Set(paths);
			if (next.has(path)) next.delete(path);
			else next.add(path);
			return next;
		});
	}, []);

	const selectEntry = useCallback(
		(entry: ProblemEntry, entryKey: string) => {
			setSelection({ groupPath: entry.path, entryKey });
			if (environmentId) {
				void navigateToProblem(entry.path, lspRangeToMonacoRange(entry.range));
			}
		},
		[environmentId],
	);

	return (
		<div
			data-testid="problems-pane"
			style={{
				display: "flex",
				flexDirection: "column",
				height: "100%",
				overflowY: "auto",
				minHeight: 0,
				padding: "8px 0",
			}}
		>
			<div style={{ display: "flex", gap: 6, flexWrap: "wrap", padding: "0 16px 8px" }}>
				<FilterChip
					severity="error"
					active={filter.error}
					onClick={() => setFilter((current) => ({ ...current, error: !current.error }))}
				/>
				<FilterChip
					severity="warning"
					active={filter.warning}
					onClick={() => setFilter((current) => ({ ...current, warning: !current.warning }))}
				/>
			</div>
			{limited.groups.map((group) => (
				<ProblemGroupRow
					key={group.path}
					group={group}
					collapsed={collapsedPaths.has(group.path)}
					onToggleCollapse={toggleCollapsed}
					selectedEntryKey={selection?.groupPath === group.path ? selection.entryKey : null}
					onSelectEntry={selectEntry}
				/>
			))}
			{limited.hiddenCount > 0 && (
				<p
					style={{
						margin: "8px 16px 0",
						fontSize: "var(--text-caption)",
						color: "var(--fg-3)",
						textAlign: "center",
					}}
				>
					他{limited.hiddenCount}件の問題
				</p>
			)}
			{limited.groups.length === 0 && (
				<p
					style={{
						margin: "auto 16px",
						fontSize: "var(--text-caption)",
						color: "var(--fg-3)",
						textAlign: "center",
					}}
				>
					{totalCount === 0 ? "問題は検出されていません" : "フィルタ条件に一致する問題はありません"}
				</p>
			)}
		</div>
	);
}

/**
 * 1ファイル分の問題グループ (トグル可能な見出し行 + エントリ一覧)。
 * memo化により、選択中のエントリの切り替えや他グループの折りたたみでは再レンダーされない
 * (selectedEntryKey は自グループの選択時のみ実値、それ以外は常に null が渡される)。
 */
const ProblemGroupRow = memo(function ProblemGroupRow({
	group,
	collapsed,
	onToggleCollapse,
	selectedEntryKey,
	onSelectEntry,
}: {
	group: ProblemFileGroup;
	collapsed: boolean;
	onToggleCollapse: (path: string) => void;
	selectedEntryKey: string | null;
	onSelectEntry: (entry: ProblemEntry, entryKey: string) => void;
}) {
	return (
		<div data-testid="problem-file-group">
			<button
				type="button"
				data-testid={`problem-file-group-toggle-${group.path}`}
				onClick={() => onToggleCollapse(group.path)}
				className={`flex w-full items-center gap-1.5 text-left cursor-pointer ${ROW_HOVER_CLASS}`}
				style={{ padding: "6px 16px", border: "none" }}
			>
				<DisclosureChevron open={!collapsed} size={11} />
				<span style={{ fontSize: "var(--text-caption)", fontWeight: 600, flexShrink: 0 }}>
					{group.fileName}
				</span>
				{group.directory && (
					<span
						title={group.directory}
						data-tooltip-if-truncated
						style={{
							minWidth: 0,
							flexShrink: 1,
							fontSize: "var(--text-caption)",
							color: "var(--fg-3)",
							overflow: "hidden",
							textOverflow: "ellipsis",
							whiteSpace: "nowrap",
						}}
					>
						{group.directory}
					</span>
				)}
				<span style={{ ...COUNT_BADGE_STYLE, flexShrink: 0 }}>
					{group.errorCount + group.warningCount}
				</span>
			</button>
			{!collapsed &&
				group.entries.map((entry, index) => {
					const entryKey = `${entry.path}:${entry.line}:${entry.column}:${entry.message}:${index}`;
					const selected = selectedEntryKey === entryKey;
					return (
						<button
							key={entryKey}
							type="button"
							onClick={() => onSelectEntry(entry, entryKey)}
							className={`flex w-full items-center text-left cursor-pointer ${
								selected ? ROW_SELECTED_CLASS : ROW_HOVER_CLASS
							}`}
							style={{
								gap: 7,
								padding: "5px 16px 5px 30px",
								border: 0,
								color: "var(--fg-1)",
								fontFamily: "inherit",
							}}
						>
							<Icon
								name={entry.severity === "error" ? "xCircle" : "alertTriangle"}
								size={14}
								color={entry.severity === "error" ? "var(--danger)" : "var(--warning)"}
							/>
							<span
								title={entry.message}
								data-tooltip-if-truncated
								style={{
									minWidth: 0,
									flex: 1,
									fontSize: "var(--text-caption)",
									overflow: "hidden",
									textOverflow: "ellipsis",
									whiteSpace: "nowrap",
								}}
							>
								{entry.message}
							</span>
							{entry.code && (
								<span style={{ ...META_TEXT_STYLE, fontSize: "var(--text-section)" }}>
									{entry.code}
								</span>
							)}
							<span style={{ ...META_TEXT_STYLE, fontSize: "var(--text-caption)" }}>
								{entry.line}:{entry.column}
							</span>
						</button>
					);
				})}
		</div>
	);
});

function FilterChip({
	severity,
	active,
	onClick,
}: {
	severity: "error" | "warning";
	active: boolean;
	onClick: () => void;
}) {
	const isError = severity === "error";
	return (
		<button
			type="button"
			data-testid={`problems-filter-${severity}`}
			onClick={onClick}
			style={{
				border: "none",
				borderRadius: "var(--radius-full)",
				padding: "3px 9px",
				fontSize: "var(--text-caption)",
				fontWeight: 500,
				fontFamily: "inherit",
				cursor: "pointer",
				...(active
					? isError
						? { background: "var(--danger-subtle)", color: "var(--danger)" }
						: {
								background: "var(--warning-subtle)",
								color: "var(--warning)",
								// 注意の地は無彩色なので、オンは縁取りを強めて示す。
								boxShadow: "var(--shadow-ring-strong)",
							}
					: NEUTRAL_FILTER_STYLE),
			}}
		>
			{isError ? "エラー" : "警告"}
		</button>
	);
}
